/**
 * UploadSnapshotPage - Full-page wizard for uploading a new snapshot to an existing dataset.
 * Replaces the old QuickUploadModal with a proper page layout for better UX.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ContentLayout from '@amzn/awsui-components-console/content-layout';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Alert from '@amzn/awsui-components-console/alert';
import Spinner from '@amzn/awsui-components-console/spinner';
import Table from '@amzn/awsui-components-console/table';
import { UploadSampleStep } from '../wizard/steps/UploadSampleStep';
import { ExecuteStep } from '../wizard/steps/ExecuteStep';
import type { Dataset, DatasetConfig } from '../../../types/starnexus';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

interface SchemaDiffResult {
  compatibility: 'identical' | 'additive' | 'breaking';
  diff: {
    added: Array<{ columnName: string; dataType: string }>;
    removed: Array<{ columnName: string; dataType: string }>;
    changed: Array<{ columnName: string; oldType: string; newType: string }>;
  };
  currentSchemaVersion?: number;
  recommendation?: string;
}

interface EvolveResult {
  ok: boolean;
  newSchemaVersion: number;
  glue: 'updated' | 'skipped' | 'failed';
  andes: 'updated' | 'skipped' | 'failed' | 'deferred';
  andesDeferReason?: string;
  warnings: Array<{ step: string; message: string; fallback?: { sql: string; dataCentralLink: string } }>;
}

function buildConfigFromDataset(dataset: Dataset): DatasetConfig {
  const anyDs = dataset as any;
  return dataset.config ?? {
    basicSettings: { datasetName: dataset.datasetName, teamId: dataset.teamId, description: dataset.description || '', processingType: dataset.processingType || 'csv-to-json' },
    schemaConfig: anyDs.schema || [],
    processingOptions: { concurrencyLimit: 10, retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 }, validation: { checkSchema: true, checkSize: true, checkDuplicates: true } },
    andesSettings: { provider: dataset.providerName, tableName: dataset.tableName, tableVersion: dataset.schemaVersion || 1, partitionKeys: anyDs.partitionKeys || [] },
    outputConfig: anyDs.outputConfig || { track: dataset.track || 'dual', loadMode: 'APPEND', partitionType: 'TIME', retentionDays: 90 },
  };
}

function inferSchemaFromPreview(preview: string[][]): Array<{ columnName: string; dataType: string }> {
  if (!preview || preview.length === 0) return [];
  return preview[0].map(h => ({ columnName: h, dataType: 'string' }));
}

export const UploadSnapshotPage: React.FC = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string[][] | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);

  // Schema diff state
  const [schemaCheck, setSchemaCheck] = useState<'pending' | 'identical' | 'additive' | 'breaking' | null>(null);
  const [schemaDiff, setSchemaDiff] = useState<SchemaDiffResult | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [evolving, setEvolving] = useState(false);
  const [evolveResult, setEvolveResult] = useState<EvolveResult | null>(null);

  // Editable new columns (user can override inferred types)
  const [editableNewCols, setEditableNewCols] = useState<Array<{ columnName: string; dataType: string }>>([]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [execCompleted, setExecCompleted] = useState(false);

  // Fetch dataset on mount
  useEffect(() => {
    if (!datasetId) return;
    apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(datasetId)}`), { ...apiFetchDefaults })
      .then(r => r.json())
      .then(data => { setDataset(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [datasetId]);

  const checkSchemaDiff = useCallback(async (preview: string[][]) => {
    if (!datasetId) return;
    setSchemaCheck('pending');
    setSchemaError(null);
    setSchemaDiff(null);
    try {
      const schema = inferSchemaFromPreview(preview);
      const res = await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(datasetId)}/schema-diff`), {
        ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schema }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const diff: SchemaDiffResult = await res.json();
      setSchemaDiff(diff);
      setSchemaCheck(diff.compatibility);
      if (diff.compatibility === 'additive' && diff.diff?.added) {
        setEditableNewCols(diff.diff.added.map((c: any) => ({ ...c })));
      }
    } catch (e: any) {
      setSchemaError(`Schema check failed: ${e.message}`);
      setSchemaCheck(null);
    }
  }, [datasetId]);

  const handleFileChange = useCallback(async (file: File | null, preview: string[][] | null, err: string | null) => {
    setUploadedFile(file);
    setFilePreview(preview);
    setFileError(err);
    setFileId(null);
    setBatchId(null);
    setSchemaCheck(null);
    setSchemaDiff(null);
    setSchemaError(null);
    setEvolveResult(null);
    if (!file || err || !datasetId) return;
    try {
      const urlRes = await apiFetch(apiUrl('/api/upload/url'), {
        ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datasetId, fileName: file.name, contentType: file.type, fileSize: file.size }),
      });
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, fileId: fId } = await urlRes.json();
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      await apiFetch(apiUrl('/api/upload/confirm'), {
        ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datasetId, fileId: fId }),
      });
      try {
        await apiFetch(apiUrl('/api/parse/file'), {
          ...apiFetchDefaults,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ datasetId, fileId: fId }),
        });
      } catch { /* non-critical */ }
      setFileId(fId);
      if (preview && preview.length > 0) checkSchemaDiff(preview);
    } catch (e: any) {
      setFileError(`Upload failed: ${e.message}`);
    }
  }, [datasetId, checkSchemaDiff]);

  const handleEvolveSchema = async () => {
    if (!filePreview || !schemaDiff?.diff?.added || !datasetId) return;
    setEvolving(true);
    setEvolveResult(null);
    try {
      const fullSchema = inferSchemaFromPreview(filePreview);
      const res = await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(datasetId)}/evolve-schema`), {
        ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schema: fullSchema, newColumns: editableNewCols }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || `HTTP ${res.status}`); }
      const data: EvolveResult = await res.json();
      setEvolveResult(data);
      const andesOk = data.andes === 'updated' || data.andes === 'skipped';
      if (data.glue === 'updated' && andesOk) {
        setSchemaCheck('identical');
        // Don't auto-proceed. Let user click "Continue to upload" so they can see what happened.
      }
    } catch (e: any) {
      setSchemaError(`Schema evolution failed: ${e.message}`);
    } finally {
      setEvolving(false);
    }
  };

  const handleGoToExecute = async () => {
    if (!fileId || !datasetId) return;
    setRegistering(true);
    setRegisterError(null);
    try {
      const r = await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(datasetId)}/batches`), {
        ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `HTTP ${r.status}`); }
      const data = await r.json();
      setBatchId(data.batchId);
    } catch (e: any) {
      setRegisterError(e.message);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) return <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>;
  if (error || !dataset) return <Alert type="error">{error || 'Dataset not found'}</Alert>;
  if (dataset.status !== 'active') {
    return (
      <ContentLayout header={<Header variant="h1">Upload not available</Header>}>
        <Alert type="warning">Dataset must be in active status to upload new snapshots. Current: {dataset.status}</Alert>
      </ContentLayout>
    );
  }

  const config = buildConfigFromDataset(dataset);
  const canProceed = fileId && !batchId && (schemaCheck === 'identical' || (evolveResult && evolveResult.ok));

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`Provider: ${dataset.providerName} | Table: ${dataset.tableName}`}
          actions={
            <Button variant="link" onClick={() => navigate(`/starnexus/uploader/datasets/${datasetId}`)}>
              Back to dataset
            </Button>
          }
        >
          Upload new snapshot to {dataset.datasetName}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {registerError && <Alert type="error" dismissible onDismiss={() => setRegisterError(null)}>{registerError}</Alert>}

        {/* Step 1: Upload */}
        {!batchId && (
          <Container header={<Header variant="h2">1. Select file</Header>}>
            <SpaceBetween size="m">
              <Alert type="info">
                Schema, SQL transform, and output configuration are inherited from the dataset.
                Upload a file with the same column structure.
              </Alert>
              <UploadSampleStep file={uploadedFile} preview={filePreview} error={fileError} onFileChange={handleFileChange} />
            </SpaceBetween>
          </Container>
        )}

        {/* Schema diff results */}
        {!batchId && schemaCheck === 'pending' && (
          <Container><Alert type="info"><Spinner /> Checking schema compatibility...</Alert></Container>
        )}
        {!batchId && schemaError && (
          <Container><Alert type="warning" dismissible onDismiss={() => setSchemaError(null)}>{schemaError}</Alert></Container>
        )}
        {!batchId && schemaCheck === 'additive' && schemaDiff?.diff?.added && !evolveResult && (
          <Container header={<Header variant="h2">Schema changed (compatible)</Header>}>
            <SpaceBetween size="m">
              <Alert type="info">New columns detected. Evolving schema is safe for downstream consumers.</Alert>
              <Table
                columnDefinitions={[
                  { id: 'col', header: 'New column', cell: (item: { columnName: string; dataType: string }) => item.columnName },
                  { id: 'type', header: 'Data type (editable)', cell: (item: { columnName: string; dataType: string }) => (
                    <select
                      value={item.dataType}
                      onChange={(e) => {
                        setEditableNewCols(prev => prev.map(c => c.columnName === item.columnName ? { ...c, dataType: e.target.value } : c));
                      }}
                      style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #aab7b8' }}
                    >
                      <option value="string">string</option>
                      <option value="integer">integer</option>
                      <option value="decimal">decimal</option>
                      <option value="boolean">boolean</option>
                      <option value="timestamp">timestamp</option>
                    </select>
                  )},
                ]}
                items={editableNewCols}
                variant="embedded"
              />
              <Box float="right" padding={{ top: 's' }}>
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => navigate(`/starnexus/uploader/datasets/${datasetId}`)}>Cancel</Button>
                  <Button variant="primary" loading={evolving} onClick={handleEvolveSchema}>Evolve schema &amp; continue</Button>
                </SpaceBetween>
              </Box>
            </SpaceBetween>
          </Container>
        )}
        {!batchId && evolveResult && (
          <Container>
            {evolveResult.glue === 'updated' ? (
              <Alert type="info">
                Schema evolved to v{evolveResult.newSchemaVersion}. DDB ✓ Glue ✓ — Andes SDL will sync automatically after upload completes.
              </Alert>
            ) : (
              <Alert type="error" header="Schema evolution failed">
                Glue table update failed. Please retry or contact support.
              </Alert>
            )}
          </Container>
        )}
        {!batchId && schemaCheck === 'breaking' && schemaDiff && (
          <Container header={<Header variant="h2">Breaking schema change</Header>}>
            <SpaceBetween size="m">
              <Alert type="error">Columns were removed or types changed. This upload cannot proceed.</Alert>
              <Table
                columnDefinitions={[
                  { id: 'col', header: 'Column', cell: (item: { columnName: string; change: string }) => item.columnName },
                  { id: 'change', header: 'Change', cell: (item: { columnName: string; change: string }) => item.change },
                ]}
                items={[
                  ...(schemaDiff.diff?.removed || []).map(c => ({ columnName: c.columnName, change: `Removed` })),
                  ...(schemaDiff.diff?.changed || []).map(c => ({ columnName: c.columnName, change: `Type changed` })),
                ]}
                variant="embedded"
              />
              <Box textAlign="right">
                <Button variant="primary" onClick={() => navigate('/starnexus/uploader/datasets/create')}>Create new dataset</Button>
              </Box>
            </SpaceBetween>
          </Container>
        )}

        {/* Schema identical confirmation */}
        {!batchId && schemaCheck === 'identical' && fileId && (
          <Container>
            <Alert type="success">Schema matches current dataset (v{schemaDiff?.currentSchemaVersion || '?'}). No evolution needed.</Alert>
          </Container>
        )}

        {/* Proceed button */}
        {!batchId && canProceed && (
          <Container>
            <Box textAlign="right">
              <Button variant="primary" loading={registering} onClick={handleGoToExecute}>
                Continue to execute
              </Button>
            </Box>
          </Container>
        )}

        {/* Step 2: Execute */}
        {batchId && (
          <Container header={<Header variant="h2">2. Execute append</Header>}>
            <SpaceBetween size="m">
              <ExecuteStep
                config={config}
                datasetId={datasetId!}
                fileId={fileId}
                mode="append"
                batchId={batchId}
                onComplete={(passed) => { if (passed) setExecCompleted(true); }}
              />
              {execCompleted && (
                <Box textAlign="right">
                  <Button variant="primary" onClick={() => navigate(`/starnexus/uploader/datasets/${datasetId}`)}>
                    Back to dataset
                  </Button>
                </Box>
              )}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default UploadSnapshotPage;
